import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ShareIcon from '@mui/icons-material/Share';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { _ } from '/@/locale';
import { downloadBagQualityPDF, emailBagQualityPDF } from '/@/store/private-data/bag-quality-pdfs/actions';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import ShareEmailModal from './share-email-modal';

/**
 * Download + share actions for a seed-quality analysis. Self-contained (like
 * ProductDatasheetShare / RecommendationShare): owns the PDF dispatch, the email
 * modal and the success/error feedback. Downloading is public; sharing by email
 * is only offered to logged-in users (`canShare`).
 */
const BagQualityShare = ({
    response,
    showDpsAnalysis,
    note,
    brand,
    canShare,
}: {
    response: Record<string, any>;
    showDpsAnalysis: boolean;
    note?: string | null;
    brand?: { id?: number; name?: string; logo?: string };
    canShare: boolean;
}) => {
    const dispatch = useDispatch();
    const [shareOpen, setShareOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const buildPayload = () => ({
        ...response,
        note: note || '',
        showDpsAnalysis,
        brandLogo: brand?.logo,
        brandName: brand?.name,
    });

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const file = await dispatch(downloadBagQualityPDF({ bagQuality: buildPayload(), download: true }));
            if (file?.url) {
                window.open(file.url);
            }
            mpTrackEvent('Seed quality analysis downloaded', { batch: response.batch, brand: brand?.name });
        } catch (error) {
            console.error('Error downloading seed quality PDF:', error);
            enqueueSnackbar(_('Ha ocurrido un error, por favor inténtalo más tarde.'), { variant: 'error' });
        } finally {
            setDownloading(false);
        }
    };

    const handleSend = async (email: string) => {
        setSending(true);
        try {
            await dispatch(emailBagQualityPDF({ bagQuality: buildPayload(), email }));
            mpTrackEvent('Seed quality analysis shared', {
                share_method: 'email',
                batch: response.batch,
                brand: brand?.name,
            });
            setShareOpen(false);
            enqueueSnackbar(_('Análisis enviado correctamente'), { variant: 'success' });
        } catch (error) {
            console.error('Error sending seed quality PDF:', error);
            enqueueSnackbar(_('Ha ocurrido un error, por favor inténtalo más tarde.'), { variant: 'error' });
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Stack direction='row' alignItems='center'>
                <Tooltip title={_('Descargar')} arrow>
                    <span>
                        <IconButton color='primary' onClick={handleDownload} disabled={downloading}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                {canShare && (
                    <Tooltip title={_('Compartir')} arrow>
                        <IconButton color='primary' onClick={() => setShareOpen(true)}>
                            <ShareIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            <ShareEmailModal
                open={shareOpen}
                sending={sending}
                onClose={() => setShareOpen(false)}
                onSend={handleSend}
            />
        </>
    );
};

export default BagQualityShare;
